import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Wrench, X, Search, User as UserIcon, ChevronDown } from 'lucide-react';
// 🚨 [데이터 서비스] 대표님이 만든 crmDataService를 사용하여 백엔드와 통신합니다.
import { crmDataService } from '../services/crmDataService'; 
import { CSActivity } from '../types'; // 🎯 타입 정의 임포트

interface CSActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName?: string; 
  // 🎯 [추가] 수정 시 기존 데이터를 받기 위한 그릇 (타입스크립트 에러 해결)
  initialData?: CSActivity; 
  onSuccess: () => void; 
}

// 🚨 한국 시간(KST) 기준 오늘 날짜 구하기 헬퍼 함수
const getTodayKST = () => {
    const now = new Date();
    const utcNow = now.getTime() + (now.getTimezoneOffset() * 60000);
    const kstTime = new Date(utcNow + (9 * 3600000));
    return kstTime.toISOString().split('T')[0];
};

export default function CSActivityModal({ isOpen, onClose, clientName, initialData, onSuccess }: CSActivityModalProps) {
  // 1. 데이터 리스트 상태
  const [csSalesReps, setCsSalesReps] = useState<any[]>([]); // 영업담당자 목록
  const [allContacts, setAllContacts] = useState<any[]>([]); // 거래처 전체 목록 (검색용)

  // 2. 검색 및 드롭다운 상태
  const [repSearch, setRepSearch] = useState(''); // 영업담당자 검색어
  const [showRepList, setShowRepList] = useState(false);
  const repDropdownRef = useRef<HTMLDivElement>(null);

  const [contactSearch, setContactSearch] = useState(''); // 거래처 검색어
  const [showContactList, setShowContactList] = useState(false);
  const contactDropdownRef = useRef<HTMLDivElement>(null);

  // 3. 입력 폼 상태 (방문 횟수 visitCount 제거)
  const [csForm, setCsForm] = useState<Partial<CSActivity>>({
    status: '접수', type: 'AS', description: '', date: '', company: '',
    contactName: '', phone: '', email: '', cost: 0, costItem: '',
    salesNote: '', salesRep: ''
  });

  // 💡 [클릭 이벤트] 드롭다운 외부 클릭 시 리스트 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (repDropdownRef.current && !repDropdownRef.current.contains(event.target as Node)) setShowRepList(false);
        if (contactDropdownRef.current && !contactDropdownRef.current.contains(event.target as Node)) setShowContactList(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 💡 [초기화] 모달이 열릴 때: initialData가 있으면 '수정모드', 없으면 '신규모드'
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // 🎯 [수정 모드] 기존 데이터를 폼에 꽉 채워줍니다.
        setCsForm(initialData);
        setContactSearch(initialData.company || '');
        setRepSearch(initialData.salesRep || '');
      } else {
        // 🎯 [신규 모드] 깨끗한 폼을 보여줍니다.
        setCsForm({
          status: '접수', type: 'AS', description: '', 
          date: getTodayKST(),
          company: clientName || '', 
          contactName: '', phone: '', email: '', cost: 0, costItem: '',
          salesNote: '', salesRep: ''
        });
        setContactSearch(clientName || '');
        setRepSearch('');
      }
      
      // 마스터 데이터 로드 (영업사원 및 거래처)
      crmDataService.getAllUsers().then(data => setCsSalesReps(Array.isArray(data) ? data : []));
      crmDataService.getContacts().then(data => setAllContacts(Array.isArray(data) ? data : []));
    }
  }, [isOpen, clientName, initialData]);

  // 🔍 [검색 필터] 거래처 및 영업담당자 실시간 필터링
  const filteredContacts = useMemo(() => {
    if (!contactSearch) return allContacts;
    return allContacts.filter(c => c.company?.toLowerCase().includes(contactSearch.toLowerCase()));
  }, [allContacts, contactSearch]);

  const filteredReps = csSalesReps.filter(rep => rep.name?.toLowerCase().includes(repSearch.toLowerCase()));

  // 🚨 [저장] CS 활동 등록/수정 실행 함수
  const handleSaveCSActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csForm.company || !csForm.description) {
        alert("거래처와 활동 상세 내용은 필수입니다.");
        return;
    }
    
    const storedData = localStorage.getItem('ieg_user_profile');
    let realName = "관리자";
    if (storedData) {
      try {
        const userData = JSON.parse(storedData);
        realName = userData.name || userData.user?.name || "관리자";
      } catch (e) {}
    }

    // 백엔드로 보낼 최종 데이터 뭉치 (visitCount는 이제 보내지 않거나 기본값 1로 고정)
    const payload = {
      ...csForm,
      cost: Number(csForm.cost || 0),
      visitCount: 1, // 🚨 방문 횟수는 이제 입력받지 않고 시스템 기본값으로 처리
      id: initialData ? initialData.id : undefined,
      contactId: initialData ? initialData.contactId : `manual_${Date.now()}`,
      createdBy: initialData ? initialData.createdBy : realName
    };

    try {
      const success = await crmDataService.saveCSActivity(payload);
      if (success) {
        alert(initialData ? "수정이 완료되었습니다." : "CS 활동이 등록되었습니다.");
        onSuccess(); 
        onClose();   
      } else {
        alert("저장에 실패했습니다.");
      }
    } catch (error) { 
        console.error(error);
        alert("네트워크 오류가 발생했습니다.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* 헤더 섹션 */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <Wrench className="text-blue-600" />
            {initialData ? 'CS 활동 기록 수정' : 'CS 활동 정식 등록'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><X size={24} className="text-slate-500" /></button>
        </div>
        
        {/* 메인 폼 섹션 */}
        <form onSubmit={handleSaveCSActivity} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase">진행 상태</label>
              <select required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" 
                value={csForm.status} onChange={(e) => setCsForm({ ...csForm, status: e.target.value as any })}>
                <option value="접수">접수</option><option value="진행">진행</option><option value="완료">완료</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase">활동 유형</label>
              <select required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" 
                value={csForm.type} onChange={(e) => setCsForm({ ...csForm, type: e.target.value as any })}>
                <option value="AS">AS (수리)</option><option value="교육">교육</option><option value="BS">BS (사전점검)</option><option value="지원">지원</option><option value="기타">기타</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase">날짜</label>
              <input type="date" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" 
                value={csForm.date} onChange={(e) => setCsForm({ ...csForm, date: e.target.value })}/>
            </div>

            {/* 🎯 거래처 정보 (잠금/검색 로직 포함) */}
            <div className="md:col-span-3 p-5 bg-blue-50/50 rounded-2xl border border-blue-100 space-y-4">
              <div className="flex items-center gap-2 text-blue-700 font-bold text-sm"><UserIcon size={16} /> 고객 및 거래처 상세 정보</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div className="space-y-1 relative" ref={contactDropdownRef}>
                  <label className="text-[10px] font-black text-slate-400 uppercase">거래처명</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <input
                      type="text"
                      placeholder="거래처 검색 또는 직접 입력..."
                      readOnly={!!clientName} 
                      className={`w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none border transition-all ${
                        !!clientName ? 'bg-slate-100 text-slate-500 cursor-not-allowed font-bold' : 'bg-white text-slate-900 focus:ring-2 focus:ring-blue-500/20'
                      }`}
                      value={contactSearch}
                      onFocus={() => !clientName && setShowContactList(true)}
                      onChange={(e) => {
                        if (!!clientName) return;
                        setContactSearch(e.target.value);
                        setCsForm({ ...csForm, company: e.target.value });
                        setShowContactList(true);
                      }}
                    />
                    {!clientName && showContactList && filteredContacts.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto py-2">
                        {filteredContacts.map(c => (
                          <button key={c.id} type="button" 
                            onClick={() => { setCsForm({...csForm, company: c.company, contactName: c.name, phone: c.phone, email: c.email, salesRep: c.salesRep}); setContactSearch(c.company); setShowContactList(false); }}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b last:border-0"
                          >
                            <span className="text-sm font-bold block">{c.company}</span>
                            <span className="text-[10px] text-slate-400">{c.name} | {c.region}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">거래처 담당자명</label>
                  <input type="text" placeholder="담당자 성함" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" 
                    value={csForm.contactName} onChange={(e) => setCsForm({ ...csForm, contactName: e.target.value })}/>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">연락처</label>
                  <input type="text" placeholder="연락처" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" 
                    value={csForm.phone} onChange={(e) => setCsForm({ ...csForm, phone: e.target.value })}/>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">이메일 주소</label>
                  <input type="email" placeholder="이메일" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20" 
                    value={csForm.email} onChange={(e) => setCsForm({ ...csForm, email: e.target.value })}/>
                </div>
              </div>
            </div>

            {/* IEG 영업 담당자 (현장 방문 횟수가 있던 자리를 IEG 담당자가 크게 차지하도록 조정) */}
            <div className="md:col-span-2 space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase">IEG 영업 담당자</label>
              <div className="relative" ref={repDropdownRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input type="text" placeholder="담당자 검색..." className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-blue-600 outline-none" 
                    value={repSearch} onFocus={() => setShowRepList(true)} 
                    onChange={(e) => { setRepSearch(e.target.value); setCsForm({ ...csForm, salesRep: e.target.value }); setShowRepList(true); }}/>
                {showRepList && filteredReps.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto py-2">
                        {filteredReps.map(rep => (
                            <button key={rep.id} type="button" 
                                onClick={() => { setCsForm({ ...csForm, salesRep: rep.name }); setRepSearch(rep.name); setShowRepList(false); }} 
                                className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b last:border-0">
                                <span className="text-sm font-bold text-slate-900">{rep.name}</span>
                            </button>
                        ))}
                    </div>
                )}
              </div>
            </div>

            <div className="space-y-2"><label className="text-xs font-black text-slate-500 uppercase">소요 비용 (원)</label>
              <input type="number" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" value={csForm.cost} onChange={(e) => setCsForm({ ...csForm, cost: Number(e.target.value) })}/>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-slate-500 uppercase">활동 상세 내용 (필수)</label>
            <textarea required rows={4} placeholder="활동 내용을 상세히 입력하세요..." className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none resize-none" value={csForm.description} onChange={(e) => setCsForm({ ...csForm, description: e.target.value })}/>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-black text-slate-500 uppercase">영업팀 전달 사항</label>
            <textarea rows={2} placeholder="영업팀 기회 요인 등..." className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none resize-none" value={csForm.salesNote} onChange={(e) => setCsForm({ ...csForm, salesNote: e.target.value })}/>
          </div>

          <div className="flex gap-4 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black hover:bg-slate-200 transition-all">취소</button>
            <button type="submit" className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20">{initialData ? '기록 수정 완료' : 'CS 활동 저장 및 피드 전송'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}